// АНИМ.1: глобальный визуальный слой для переходов.
//
// Слушает useTransitionStore и:
//   1. Управляет CSS-классами на <body> (для filter/opacity/transform
//      keyframes-анимаций в transitions.css).
//   2. Рендерит SVG-defs (feColorMatrix + feTurbulence) — нужны как
//      базовая инфраструктура для будущих feColorMatrix-фильтров
//      (сейчас RGB-split реализован через CSS drop-shadow для прода).
//   3. Рисует чёрный middle-кадр поверх всего (когда phase === 'middle').
//   4. Рисует pixel-sort bars (только heavy_glitch на outgoing/incoming).
//
// Сам не управляет таймингами — это делает useScreenTransition через
// setPhase. Все стили — в transitions.css.

import React from 'react';
import { createPortal } from 'react-dom';
import { useTransitionStore } from './transitionStore';
import { VARIANT_TIMINGS, ALL_TX_CLASSES } from './types';
import './transitions.css';

export function TransitionOverlay() {
  const phase = useTransitionStore((s) => s.phase);
  const variant = useTransitionStore((s) => s.variant);

  // Управление классами на body: синхронно удаляем все возможные,
  // добавляем текущий. Это гарантирует что между phase-переходами
  // не остаётся "пустого кадра" без класса (animation бы сбросилась).
  React.useEffect(() => {
    ALL_TX_CLASSES.forEach((c) => document.body.classList.remove(c));
    if (phase !== 'idle') {
      document.body.classList.add(`tx-${phase}-${variant}`);
    }
    return () => {
      ALL_TX_CLASSES.forEach((c) => document.body.classList.remove(c));
    };
  }, [phase, variant]);

  // Portal в body — оверлей должен быть СНАРУЖИ #root, иначе CSS-анимация
  // opacity:0 на #root скрывала бы и сам оверлей.
  const overlay = (
    <>
      <SvgDefs />
      {phase === 'middle' && <MiddleFrame />}
      {(phase === 'outgoing' || phase === 'incoming') &&
        VARIANT_TIMINGS[variant].pixelSort && (
          <PixelSortBars phaseKey={`${phase}-${variant}`} />
        )}
    </>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(overlay, document.body);
}

// SVG-defs всегда смонтированы. feTurbulence используется в MiddleFrame для
// noise-overlay. RGB-split фильтры тут как зарезервированные ID — на случай
// если в проде drop-shadow окажется тяжёлым, переключимся на feColorMatrix.
function SvgDefs() {
  return (
    <svg
      aria-hidden
      style={{ position: 'fixed', width: 0, height: 0, pointerEvents: 'none' }}
    >
      <defs>
        <filter id="tx-noise" x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            stitchTiles="stitch"
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.18 0"
          />
        </filter>
        {/* RGB-split через feColorMatrix + feOffset (для будущей миграции). */}
        {[6, 12].map((dx) => (
          <filter
            key={dx}
            id={`tx-rgb-${dx}`}
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
          >
            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
              result="r"
            />
            <feOffset in="r" dx={-dx} dy="0" result="ro" />
            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="0 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0"
              result="gb"
            />
            <feOffset in="gb" dx={dx} dy="0" result="gbo" />
            <feBlend in="ro" in2="gbo" mode="screen" />
          </filter>
        ))}
      </defs>
    </svg>
  );
}

function MiddleFrame() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        zIndex: 99998,
        pointerEvents: 'none'
      }}
    >
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.08
        }}
      >
        <rect width="100%" height="100%" filter="url(#tx-noise)" />
      </svg>
    </div>
  );
}

// 3 горизонтальные полосы с jitter. На каждое монтирование — новые позиции
// (useMemo по phaseKey, который меняется при смене фазы/варианта).
function PixelSortBars({ phaseKey }: { phaseKey: string }) {
  const bars = React.useMemo(
    () =>
      [0, 1, 2].map((i) => ({
        key: `${i}-${Math.random()}`,
        top: 8 + Math.random() * 80,        // 8..88%
        height: 6 + Math.random() * 12,     // 6..18px
        jitter: (Math.random() - 0.5) * 40, // -20..+20px
        delay: Math.random() * 80           // 0..80мс
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phaseKey]
  );

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99997,
        pointerEvents: 'none',
        overflow: 'hidden',
        mixBlendMode: 'screen' as React.CSSProperties['mixBlendMode']
      }}
    >
      {bars.map((b) => (
        <div
          key={b.key}
          style={{
            position: 'absolute',
            left: -100,
            right: -100,
            top: `${b.top}%`,
            height: b.height,
            background: 'rgba(255, 255, 255, 0.35)',
            animation: `tx-bar-jitter 50ms ${b.delay}ms steps(3) infinite`,
            ['--bar-dx' as string]: `${b.jitter}px`
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}
